import { Button, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import { LoginCountLimits, loginCountApi } from 'apis/login-count';

import { Role } from 'models/user';

import { useToasts, useTranslate } from 'hooks/common';
import { useCurrentRole } from 'hooks/components/auth';

// Rows share one limits request, so they all receive the same error object. Report it once.
const reportedLimitsErrors = new WeakSet<object>();

const getErrorMessage = (error: unknown, fallback: string) => {
  const message = (error as { message?: string } | null)?.message;
  return message || fallback;
};

interface LoginCountInfoProps {
  limitType: keyof LoginCountLimits;
  userId: number;
  loginCount?: number | null;
  onReset: () => void;
}

export const LoginCountInfo = ({ limitType, userId, loginCount, onReset }: LoginCountInfoProps) => {
  const role = useCurrentRole();
  const t = useTranslate();
  const { notiSuccess, notiFailed } = useToasts();
  const [count, setCount] = useState(loginCount);
  const [isResetting, setIsResetting] = useState(false);
  const [limit, setLimit] = useState<number | null>(null);
  // Guards the gap between a click and the re-render that disables the button.
  const isResetPending = useRef(false);
  const canReset = role === Role.Internal || role === Role.SuperAdmin;

  useEffect(() => {
    if (!canReset) return;
    let active = true;
    loginCountApi
      .getLimits()
      .then((limits) => active && setLimit(limits[limitType]))
      .catch((error) => {
        if (typeof error === 'object' && error !== null) {
          if (reportedLimitsErrors.has(error)) return;
          reportedLimitsErrors.add(error);
        }
        notiFailed(getErrorMessage(error, t('message.actionFailed')));
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReset, limitType]);

  useEffect(() => {
    setCount(loginCount);
  }, [userId, loginCount]);

  const handleReset = async () => {
    if (!canReset || isResetPending.current) return;
    isResetPending.current = true;
    setIsResetting(true);
    try {
      const user = await loginCountApi.reset(userId);
      setCount(user.login_count);
      notiSuccess(t('message.loginCountResetSuccess'));
      onReset();
    } catch (error) {
      notiFailed(getErrorMessage(error, t('message.actionFailed')));
    } finally {
      isResetPending.current = false;
      setIsResetting(false);
    }
  };

  return (
    <VStack align="start" spacing="2">
      <Text>{count ?? '-'}</Text>
      {canReset && limit !== null && count != null && count >= limit && (
        <Button
          size="sm"
          colorScheme="tango"
          variant="outline"
          isLoading={isResetting}
          onClick={(event) => {
            event.stopPropagation();
            handleReset();
          }}
        >
          {t('button.resetLoginCount')}
        </Button>
      )}
    </VStack>
  );
};
